import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { 
  Edit3, Clock, CheckCircle, XCircle, Search, RotateCcw, Calendar as CalendarIcon, 
  User, FileText, Building2, Filter, Eye, History, GitBranch,
  AlertCircle, Loader2, Timer, ArrowRight, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE CONFIGURATIONS
// ═══════════════════════════════════════════════════════════════════════════════

const TIMESHEET_STATUS_BADGE = {
  corrected_pending_reapproval: { label: 'Needs Reapproval', className: 'bg-orange-500 text-white', icon: RotateCcw },
  reapproved: { label: 'Reapproved', className: 'bg-green-600 text-white', icon: CheckCircle },
  pending_approval: { label: 'Pending', className: 'bg-yellow-500 text-black', icon: Clock },
  approved_scheduled: { label: 'Approved', className: 'bg-green-600 text-white', icon: CheckCircle },
  approved_adjusted: { label: 'Approved (Adj)', className: 'bg-green-500 text-white', icon: CheckCircle },
  rejected: { label: 'Rejected', className: 'bg-red-600 text-white', icon: XCircle },
  returned_for_correction: { label: 'Returned', className: 'bg-orange-500 text-white', icon: RotateCcw },
};

const SHIFT_STATUS_BADGE = {
  COMPLETED: { label: 'Completed', className: 'bg-green-600 text-white' },
  completed: { label: 'Completed', className: 'bg-green-600 text-white' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-500 text-white' },
  ASSIGNED: { label: 'Assigned', className: 'bg-yellow-500 text-black' },
  CANCELLED: { label: 'Cancelled', className: 'bg-gray-500 text-white' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  return timeStr;
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleString('en-AU', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit' 
    });
  } catch {
    return isoString;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// BADGE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const TimesheetStatusBadge = ({ status }) => {
  const config = TIMESHEET_STATUS_BADGE[status] || { label: status || '-', className: 'bg-gray-500 text-white', icon: Clock };
  const Icon = config.icon || Clock;
  return (
    <Badge className={`${config.className} text-xs flex items-center gap-1`} data-testid="timesheet-status-badge">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const ShiftStatusBadge = ({ status }) => {
  const config = SHIFT_STATUS_BADGE[status] || { label: status || '-', className: 'bg-gray-500 text-white' };
  return (
    <Badge className={`${config.className} text-xs`} data-testid="shift-status-badge">
      {config.label}
    </Badge>
  );
};

const VersionBadge = ({ version, isCorrection, isSuperseded }) => {
  if (isSuperseded) {
    return (
      <Badge variant="outline" className="text-xs text-gray-500 border-gray-300" data-testid="version-badge">
        v{version} (Superseded)
      </Badge>
    );
  }
  if (isCorrection) {
    return (
      <Badge className="bg-purple-600 text-white text-xs" data-testid="version-badge">
        v{version} (Correction)
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs" data-testid="version-badge">
      v{version || 1}
    </Badge>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// VERSION HISTORY DRAWER
// ═══════════════════════════════════════════════════════════════════════════════

const VersionHistoryDrawer = ({ entityType, entityId, open, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !entityId) return;
    
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const endpoint = entityType === 'shift' 
          ? `/corrections/shifts/${entityId}/versions`
          : `/corrections/timesheets/${entityId}/versions`;
        const res = await api.get(endpoint);
        setVersions(res.data.data || []);
      } catch (e) {
        toast.error('Failed to load version history');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVersions();
  }, [open, entityId, entityType]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="version-history-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Version History
          </SheetTitle>
          <SheetDescription>
            {entityType === 'shift' ? 'Shift' : 'Timesheet'} correction history
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No version history found</p>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
              
              {versions.map((version, idx) => (
                <div key={version.id} className="relative pl-10 pb-6" data-testid={`version-${version.version || 1}`}>
                  {/* Timeline dot */}
                  <div className={cn(
                    "absolute left-2.5 w-3 h-3 rounded-full border-2",
                    version.is_superseded ? "bg-gray-300 border-gray-400" : 
                    version.is_correction ? "bg-purple-500 border-purple-600" : 
                    "bg-green-500 border-green-600"
                  )} />
                  
                  <Card className={cn(version.is_superseded && "opacity-60")}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          Version {version.version || 1}
                          <VersionBadge 
                            version={version.version || 1} 
                            isCorrection={version.is_correction} 
                            isSuperseded={version.is_superseded} 
                          />
                        </CardTitle>
                        {idx === 0 && !version.is_superseded && (
                          <Badge className="bg-blue-600 text-white text-xs">Current</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {entityType === 'shift' ? (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <p className="text-muted-foreground text-xs">Date</p>
                              <p className="font-mono">{version.date}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Time</p>
                              <p className="font-mono">{formatTime(version.start_time)} - {formatTime(version.end_time)}</p>
                            </div>
                          </div>
                          {version.break_duration > 0 && (
                            <div>
                              <p className="text-muted-foreground text-xs">Break</p>
                              <p>{version.break_duration} minutes</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <p className="text-muted-foreground text-xs">Hours</p>
                              <p className="font-mono font-bold">{(version.hours_worked || 0).toFixed(2)}h</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Status</p>
                              <TimesheetStatusBadge status={version.status} />
                            </div>
                          </div>
                        </>
                      )}
                      
                      {version.correction_reason && (
                        <div className="pt-2 border-t">
                          <p className="text-muted-foreground text-xs">Correction Reason</p>
                          <p className="bg-orange-50 rounded p-2 text-xs text-orange-800">{version.correction_reason}</p>
                        </div>
                      )}
                      
                      {version.corrected_at && (
                        <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
                          <span>Corrected by {version.corrected_by_name || 'Admin'}</span>
                          <span className="font-mono">{formatDateTime(version.corrected_at)}</span>
                        </div>
                      )}
                      
                      {!version.corrected_at && version.created_at && (
                        <div className="text-xs text-muted-foreground pt-2 border-t">
                          Created: {formatDateTime(version.created_at)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT COMPLETED SHIFT DIALOG
// ═══════════════════════════════════════════════════════════════════════════════

const EditCompletedShiftDialog = ({ shift, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [form, setForm] = useState({
    start_time: '',
    end_time: '',
    break_duration: '',
    notes: '',
    edit_reason: '',
  });

  useEffect(() => {
    if (shift && open) {
      setForm({
        start_time: shift.start_time || '',
        end_time: shift.end_time || '',
        break_duration: shift.break_duration?.toString() || '0',
        notes: shift.notes || '',
        edit_reason: '',
      });
      setValidation(null);
    }
  }, [shift, open]);

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await api.post(`/corrections/shifts/${shift.id}/validate`, {
        start_time: form.start_time || undefined,
        end_time: form.end_time || undefined,
        break_duration: form.break_duration ? parseInt(form.break_duration) : undefined,
      });
      setValidation(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (form.edit_reason.length < 10) {
      toast.error('Correction reason must be at least 10 characters');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        edit_reason: form.edit_reason,
      };
      
      // Only include changed fields
      if (form.start_time !== shift.start_time) payload.start_time = form.start_time;
      if (form.end_time !== shift.end_time) payload.end_time = form.end_time;
      if (parseInt(form.break_duration) !== (shift.break_duration || 0)) {
        payload.break_duration = parseInt(form.break_duration) || 0;
      }
      if (form.notes !== (shift.notes || '')) payload.notes = form.notes;
      
      const res = await api.post(`/corrections/shifts/${shift.id}/edit-completed`, payload);
      toast.success(res.data.message || 'Shift corrected successfully');
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Correction failed');
    } finally {
      setLoading(false);
    }
  };

  if (!shift) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-shift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Completed Shift
          </DialogTitle>
          <DialogDescription>
            Correct shift times for {shift.date}. This will create a new version.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Values */}
          <Card className="bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Current Values</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Start</p>
                <p className="font-mono">{shift.start_time}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">End</p>
                <p className="font-mono">{shift.end_time}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Break</p>
                <p className="font-mono">{shift.break_duration || 0}m</p>
              </div>
            </CardContent>
          </Card>

          {/* New Values */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">New Start Time</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="edit-start-time"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New End Time</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="edit-end-time"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Break (mins)</Label>
              <Input
                type="number"
                min="0"
                value={form.break_duration}
                onChange={(e) => setForm({ ...form, break_duration: e.target.value })}
                data-testid="edit-break"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Additional notes..."
              data-testid="edit-notes"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Correction Reason (Required, min 10 chars)</Label>
            <Textarea
              value={form.edit_reason}
              onChange={(e) => setForm({ ...form, edit_reason: e.target.value })}
              rows={2}
              placeholder="Explain why this correction is needed..."
              className={cn(form.edit_reason.length > 0 && form.edit_reason.length < 10 && "border-red-500")}
              data-testid="edit-reason"
            />
            <p className="text-xs text-muted-foreground">{form.edit_reason.length}/10 characters</p>
          </div>

          {/* Validation Results */}
          {validation && (
            <Card className={cn(
              "border",
              validation.valid ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50"
            )}>
              <CardContent className="p-3 space-y-2">
                {validation.issues?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-red-700">Issues:</p>
                    <ul className="list-disc list-inside text-xs text-red-600">
                      {validation.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                    </ul>
                  </div>
                )}
                {validation.warnings?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-orange-700">Warnings:</p>
                    <ul className="list-disc list-inside text-xs text-orange-600">
                      {validation.warnings.map((warn, i) => <li key={i}>{warn}</li>)}
                    </ul>
                  </div>
                )}
                {validation.valid && validation.issues?.length === 0 && (
                  <p className="text-xs text-green-700 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Validation passed
                  </p>
                )}
                {validation.has_timesheet && (
                  <p className="text-xs text-blue-700 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Linked timesheet will need regeneration
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleValidate} disabled={validating}>
            {validating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
            Validate
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || form.edit_reason.length < 10}
            data-testid="submit-correction"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit3 className="h-4 w-4 mr-2" />}
            Apply Correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT TIMESHEET AFTER APPROVAL DIALOG
// ═══════════════════════════════════════════════════════════════════════════════

const EditTimesheetDialog = ({ timesheet, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    hours_worked: '',
    actual_start: '',
    actual_end: '',
    break_minutes: '',
    notes: '',
    edit_reason: '',
  });

  useEffect(() => {
    if (timesheet && open) {
      setForm({
        hours_worked: timesheet.hours_worked?.toString() || '',
        actual_start: timesheet.actual_start || timesheet.actual_clock_in_time || '',
        actual_end: timesheet.actual_end || timesheet.actual_clock_out_time || '',
        break_minutes: timesheet.break_minutes?.toString() || '0',
        notes: timesheet.notes || '',
        edit_reason: '',
      });
    }
  }, [timesheet, open]);

  const handleSubmit = async () => {
    if (form.edit_reason.length < 10) {
      toast.error('Correction reason must be at least 10 characters');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        edit_reason: form.edit_reason,
      };
      
      // Only include changed fields
      if (form.hours_worked && parseFloat(form.hours_worked) !== timesheet.hours_worked) {
        payload.hours_worked = parseFloat(form.hours_worked);
      }
      if (form.actual_start) payload.actual_start = form.actual_start;
      if (form.actual_end) payload.actual_end = form.actual_end;
      if (form.break_minutes) payload.break_minutes = parseInt(form.break_minutes) || 0;
      if (form.notes) payload.notes = form.notes;
      
      const res = await api.post(`/corrections/timesheets/${timesheet.id}/edit-after-approval`, payload);
      toast.success(res.data.message || 'Timesheet corrected. Reapproval required.');
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Correction failed');
    } finally {
      setLoading(false);
    }
  };

  if (!timesheet) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-timesheet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Approved Timesheet
          </DialogTitle>
          <DialogDescription>
            Edit timesheet for {timesheet.date}. This will require reapproval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert className="border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              Editing this timesheet will change its status to "Needs Reapproval" and create a new version.
            </AlertDescription>
          </Alert>

          {/* Current Values */}
          <Card className="bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Current Values</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Hours Worked</p>
                <p className="font-mono font-bold">{(timesheet.hours_worked || 0).toFixed(2)}h</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <TimesheetStatusBadge status={timesheet.status || timesheet.approval_status} />
              </div>
            </CardContent>
          </Card>

          {/* New Values */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Hours Worked</Label>
              <Input
                type="number"
                step="0.25"
                min="0"
                value={form.hours_worked}
                onChange={(e) => setForm({ ...form, hours_worked: e.target.value })}
                data-testid="edit-hours"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Break (mins)</Label>
              <Input
                type="number"
                min="0"
                value={form.break_minutes}
                onChange={(e) => setForm({ ...form, break_minutes: e.target.value })}
                data-testid="edit-break-mins"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Additional notes..."
              data-testid="edit-ts-notes"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Correction Reason (Required, min 10 chars)</Label>
            <Textarea
              value={form.edit_reason}
              onChange={(e) => setForm({ ...form, edit_reason: e.target.value })}
              rows={2}
              placeholder="Explain why this correction is needed..."
              className={cn(form.edit_reason.length > 0 && form.edit_reason.length < 10 && "border-red-500")}
              data-testid="edit-ts-reason"
            />
            <p className="text-xs text-muted-foreground">{form.edit_reason.length}/10 characters</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || form.edit_reason.length < 10}
            data-testid="submit-ts-correction"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit3 className="h-4 w-4 mr-2" />}
            Apply Correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// REAPPROVAL DIALOG
// ═══════════════════════════════════════════════════════════════════════════════

const ReapprovalDialog = ({ timesheet, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const handleReapprove = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/corrections/timesheets/${timesheet.id}/reapprove`, {
        approval_notes: notes,
      });
      toast.success(res.data.message || 'Timesheet reapproved successfully');
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reapproval failed');
    } finally {
      setLoading(false);
    }
  };

  if (!timesheet) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="reapproval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Reapprove Timesheet
          </DialogTitle>
          <DialogDescription>
            Confirm reapproval for the corrected timesheet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-mono">{timesheet.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Staff</span>
                <span>{timesheet.staff_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Hours</span>
                <span className="font-mono font-bold">{(timesheet.hours_worked || 0).toFixed(2)}h</span>
              </div>
              {timesheet.version && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Version</span>
                  <VersionBadge version={timesheet.version} isCorrection={timesheet.is_correction} />
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-1">
            <Label className="text-xs">Approval Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Add any approval notes..."
              data-testid="reapproval-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleReapprove} disabled={loading} className="bg-green-600 hover:bg-green-700" data-testid="confirm-reapproval">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Reapprove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function CorrectionsPage() {
  const { user, permissions } = useAuth();
  const [pendingReapproval, setPendingReapproval] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ enabled: false });
  
  // Dialog states
  const [selectedShift, setSelectedShift] = useState(null);
  const [editShiftOpen, setEditShiftOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [editTimesheetOpen, setEditTimesheetOpen] = useState(false);
  const [reapprovalOpen, setReapprovalOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionEntity, setVersionEntity] = useState({ type: null, id: null });

  // Check permissions
  const hasEditShiftPermission = permissions?.includes('shift.edit_post_completion') || 
    ['Super Admin', 'Admin'].includes(user?.role_name);
  const hasEditTimesheetPermission = permissions?.includes('timesheet.edit_after_approval') || 
    ['Super Admin', 'Admin'].includes(user?.role_name);
  const hasReapprovePermission = permissions?.includes('timesheet.reapprove_after_edit') || 
    permissions?.includes('timesheets.approve') ||
    ['Super Admin', 'Admin', 'HR', 'Accounts', 'Payroll Manager'].includes(user?.role_name);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/corrections/config');
      setConfig(res.data);
    } catch (e) {
      // Module not enabled
      setConfig({ enabled: false });
    }
  }, []);

  const fetchPendingReapproval = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/corrections/pending-reapproval');
      setPendingReapproval(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load pending reapprovals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchPendingReapproval();
  }, [fetchConfig, fetchPendingReapproval]);

  const handleViewVersionHistory = (type, id) => {
    setVersionEntity({ type, id });
    setVersionHistoryOpen(true);
  };

  const handleEditTimesheet = (ts) => {
    setSelectedTimesheet(ts);
    setEditTimesheetOpen(true);
  };

  const handleReapprove = (ts) => {
    setSelectedTimesheet(ts);
    setReapprovalOpen(true);
  };

  const handleSuccess = () => {
    fetchPendingReapproval();
  };

  if (!config.enabled) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Post-Completion Edit Not Enabled</h2>
            <p className="text-muted-foreground">
              The post-completion shift edit feature is not enabled. Contact your administrator to enable this feature.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="corrections-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Shift & Timesheet Corrections</h1>
          <p className="text-muted-foreground mt-1">Manage post-completion edits and reapprovals</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPendingReapproval}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Info Alert */}
      <Alert className="border-blue-200 bg-blue-50">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          All corrections create new versions. Original records are preserved for audit compliance.
          Edited timesheets require reapproval before they can be invoiced.
        </AlertDescription>
      </Alert>

      {/* Pending Reapproval Section */}
      <Card data-testid="pending-reapproval-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-orange-500" />
            Pending Reapproval
            {pendingReapproval.length > 0 && (
              <Badge className="bg-orange-500 text-white">{pendingReapproval.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Timesheets that have been edited and require reapproval
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : pendingReapproval.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p>No timesheets pending reapproval</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead>Participant</TableHead>
                  <TableHead className="text-center">Hours</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Corrected At</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingReapproval.map((ts) => (
                  <TableRow key={ts.id} data-testid={`pending-row-${ts.id}`}>
                    <TableCell className="font-mono">{ts.date}</TableCell>
                    <TableCell>{ts.staff_name || 'N/A'}</TableCell>
                    <TableCell>{ts.participant_name || 'N/A'}</TableCell>
                    <TableCell className="text-center font-mono font-bold">
                      {(ts.hours_worked || 0).toFixed(2)}h
                    </TableCell>
                    <TableCell>
                      <VersionBadge version={ts.version} isCorrection={ts.is_correction} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateTime(ts.corrected_at)}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs">
                      {ts.correction_reason || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewVersionHistory('timesheet', ts.id)}
                          data-testid={`view-history-${ts.id}`}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        {hasEditTimesheetPermission && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTimesheet(ts)}
                            data-testid={`edit-ts-${ts.id}`}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                        )}
                        {hasReapprovePermission && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleReapprove(ts)}
                            data-testid={`reapprove-${ts.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Reapprove
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" data-testid="quick-action-shifts">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-100">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Edit Completed Shifts</h3>
              <p className="text-sm text-muted-foreground">
                Correct shift times after clock-in/out. Navigate to the shift detail page.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-primary/50 transition-colors" data-testid="quick-action-timesheets">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-orange-100">
              <FileText className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold">Edit Approved Timesheets</h3>
              <p className="text-sm text-muted-foreground">
                Correct timesheets after approval. Use Timesheet Approval dashboard.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <EditCompletedShiftDialog
        shift={selectedShift}
        open={editShiftOpen}
        onClose={() => setEditShiftOpen(false)}
        onSuccess={handleSuccess}
      />

      <EditTimesheetDialog
        timesheet={selectedTimesheet}
        open={editTimesheetOpen}
        onClose={() => setEditTimesheetOpen(false)}
        onSuccess={handleSuccess}
      />

      <ReapprovalDialog
        timesheet={selectedTimesheet}
        open={reapprovalOpen}
        onClose={() => setReapprovalOpen(false)}
        onSuccess={handleSuccess}
      />

      <VersionHistoryDrawer
        entityType={versionEntity.type}
        entityId={versionEntity.id}
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
      />
    </div>
  );
}

// Export components for use in other pages
export { 
  EditCompletedShiftDialog, 
  EditTimesheetDialog, 
  ReapprovalDialog, 
  VersionHistoryDrawer,
  VersionBadge,
  TimesheetStatusBadge 
};
